import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as KeyIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Cloud as CloudIcon,
  Settings as SettingsIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { ApiKeysService, ApiKey, CreateApiKeyData } from '../../../services/apiKeysService';
import { useAuthStore } from '../../../store/authStore';
import ApiTestPopup from './MistralTestPopup';
import { useTranslation } from '../../../contexts/TranslationContext';

const ApiKeysTab: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  
  // State management
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testApiKey, setTestApiKey] = useState<string>('');
  const [testService, setTestService] = useState<string>('mistral');
  
  // Form states
  const [formData, setFormData] = useState<CreateApiKeyData>({
    service: 'groq',
    api_key: '',
    company_id: undefined,
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  
  // Get company context from URL
  const companyId = searchParams.get('company');
  const isCompanyDashboard = !!companyId;

  // Service options
  const serviceOptions = ApiKeysService.getServiceOptions();

  // Load API keys on component mount
  useEffect(() => {
    loadApiKeys();
  }, [companyId]);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let keys: ApiKey[];
      if (isCompanyDashboard && companyId) {
        keys = await ApiKeysService.getCompanyApiKeys(companyId);
      } else {
        keys = await ApiKeysService.getAllUserApiKeys();
      }
      
      setApiKeys(keys);
    } catch (err) {
      console.error('Error loading API keys:', err);
      setError(t('settings.apiKeys.toast.loadFailed'));
      toast.error(t('settings.apiKeys.toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddApiKey = async () => {
    if (!formData.service || !formData.api_key.trim()) {
      toast.error(t('settings.apiKeys.add.required'));
      return;
    }

    try {
      setFormLoading(true);
      
      const newApiKey = await ApiKeysService.createApiKey({
        ...formData,
        company_id: isCompanyDashboard ? companyId : undefined,
      });
      
      // Add new key to the list
      setApiKeys(prev => [newApiKey, ...prev]);
      setAddDialogOpen(false);
      setFormData({ service: 'groq', api_key: '', company_id: undefined });
      toast.success(t('settings.apiKeys.toast.added'));
    } catch (err: any) {
      console.error('Error adding API key:', err);
      toast.error(err.message || t('settings.apiKeys.toast.addFailed'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditApiKey = async () => {
    if (!selectedApiKey || !formData.api_key.trim()) {
      toast.error(t('settings.apiKeys.add.required'));
      return;
    }

    try {
      setFormLoading(true);
      
      const updatedApiKey = await ApiKeysService.updateApiKey(selectedApiKey.id, {
        api_key: formData.api_key,
      });
      
      setApiKeys(prev => prev.map(key => key.id === selectedApiKey.id ? updatedApiKey : key));
      setEditDialogOpen(false);
      setSelectedApiKey(null);
      setFormData({ service: 'groq', api_key: '', company_id: undefined });
      toast.success(t('settings.apiKeys.toast.updated'));
    } catch (err: any) {
      console.error('Error updating API key:', err);
      toast.error(err.message || t('settings.apiKeys.toast.updateFailed'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteApiKey = async () => {
    if (!selectedApiKey) return;

    try {
      setFormLoading(true);
      
      await ApiKeysService.deleteApiKey(selectedApiKey.id);
      setApiKeys(prev => prev.filter(key => key.id !== selectedApiKey.id));
      setDeleteDialogOpen(false);
      setSelectedApiKey(null);
      toast.success(t('settings.apiKeys.toast.deleted'));
    } catch (err: any) {
      console.error('Error deleting API key:', err);
      toast.error(err.message || t('settings.apiKeys.toast.deleteFailed'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (apiKey: ApiKey) => {
    try {
      const updatedApiKey = await ApiKeysService.toggleApiKeyStatus(apiKey.id);
      setApiKeys(prev => prev.map(key => key.id === apiKey.id ? updatedApiKey : key));
      const stateText = updatedApiKey.is_active ? t('settings.apiKeys.toggled.activated') : t('settings.apiKeys.toggled.deactivated');
      toast.success(t('settings.apiKeys.toast.toggled').replace('{state}', stateText));
    } catch (err: any) {
      console.error('Error toggling API key status:', err);
      toast.error(err.message || t('settings.apiKeys.toast.updateFailed'));
    }
  };

  const openEditDialog = (apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);
    setFormData({
      service: apiKey.service,
      api_key: apiKey.api_key,
      company_id: apiKey.company_id,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);
    setDeleteDialogOpen(true);
  };

  const openTestDialog = (apiKey: ApiKey) => {
    setTestApiKey(apiKey.api_key);
    setTestService(apiKey.service);
    setTestDialogOpen(true);
  };

  const getServiceIcon = (service: string) => {
    switch (service) {
      case 'groq':
        return <CloudIcon sx={{ color: '#00B4D8' }} />;
      case 'mistral':
        return <CloudIcon sx={{ color: '#6366F1' }} />;
      case 'openai':
        return <CloudIcon sx={{ color: '#10A37F' }} />;
      case 'anthropic':
        return <CloudIcon sx={{ color: '#D97706' }} />;
      case 'google':
        return <CloudIcon sx={{ color: '#4285F4' }} />;
      case 'azure':
        return <CloudIcon sx={{ color: '#0078D4' }} />;
      default:
        return <SettingsIcon sx={{ color: '#6B7280' }} />;
    }
  };

  const getServiceColor = (service: string) => {
    switch (service) {
      case 'groq':
        return '#00B4D8';
      case 'mistral':
        return '#6366F1';
      case 'openai':
        return '#10A37F';
      case 'anthropic':
        return '#D97706';
      case 'google':
        return '#4285F4';
      case 'azure':
        return '#0078D4';
      default:
        return '#6B7280';
    }
  };

  const maskApiKey = (key: string) => {
    if (key.length <= 8) return '••••••••';
    return key.substring(0, 4) + '••••••••' + key.substring(key.length - 4);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography>{t('settings.apiKeys.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
            {t('settings.apiKeys.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {isCompanyDashboard 
              ? t('settings.apiKeys.subtitle.company')
              : t('settings.apiKeys.subtitle.user')
            }
          </Typography>
        </Box>

        {/* Add API Key Button */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('settings.apiKeys.addNew')}
          </Button>
          
          {isCompanyDashboard && (
            <Chip
              icon={<BusinessIcon />}
              label={t('settings.apiKeys.chip.companyDashboard')}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* API Keys List */}
        <AnimatePresence>
          {apiKeys.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: 'center',
                  borderRadius: 3,
                  border: '2px dashed rgba(0, 0, 0, 0.1)',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)',
                }}
              >
                <KeyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                  {t('settings.apiKeys.empty.title')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  {isCompanyDashboard 
                    ? t('settings.apiKeys.empty.subtitle.company')
                    : t('settings.apiKeys.empty.subtitle.user')
                  }
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setAddDialogOpen(true)}
                  sx={{ borderRadius: 2 }}
                >
                  {t('settings.apiKeys.empty.add')}
                </Button>
              </Paper>
            </motion.div>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                },
                gap: 3,
              }}
            >
              {apiKeys.map((apiKey, index) => (
                <motion.div
                  key={apiKey.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {getServiceIcon(apiKey.service)}
                        <Box sx={{ ml: 2, flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                            {serviceOptions.find(s => s.value === apiKey.service)?.label || apiKey.service}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            {apiKey.company_id ? (
                              <Chip
                                icon={<BusinessIcon />}
                                label={t('settings.apiKeys.label.company')}
                                size="small"
                                sx={{ backgroundColor: `${getServiceColor(apiKey.service)}20`, color: getServiceColor(apiKey.service) }}
                              />
                            ) : (
                              <Chip
                                icon={<PersonIcon />}
                                label={t('settings.apiKeys.label.personal')}
                                size="small"
                                sx={{ backgroundColor: `${getServiceColor(apiKey.service)}20`, color: getServiceColor(apiKey.service) }}
                              />
                            )}
                            <Chip
                              label={apiKey.is_active ? t('settings.apiKeys.status.active') : t('settings.apiKeys.status.inactive')}
                              size="small"
                              color={apiKey.is_active ? 'success' : 'default'}
                              variant={apiKey.is_active ? 'filled' : 'outlined'}
                            />
                          </Box>
                        </Box>
                      </Box>

                      {/* API Key */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                          {t('settings.apiKeys.field.apiKey')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              backgroundColor: 'rgba(0, 0, 0, 0.05)',
                              px: 2,
                              py: 1,
                              borderRadius: 1,
                              flex: 1,
                              wordBreak: 'break-all',
                            }}
                          >
                            {maskApiKey(apiKey.api_key)}
                          </Typography>
                          <Tooltip title={showApiKey ? t('settings.apiKeys.tooltip.hide') : t('settings.apiKeys.tooltip.show')}>
                            <IconButton
                              size="small"
                              onClick={() => setShowApiKey(!showApiKey)}
                            >
                              {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Usage Info */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                          {t('settings.apiKeys.usage.usedBy').replace('{count}', String(apiKey.is_used_by?.length || 0))}
                        </Typography>
                        {apiKey.last_used_at && (
                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            {t('settings.apiKeys.usage.lastUsed').replace('{date}', new Date(apiKey.last_used_at).toLocaleDateString())}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Box sx={{ display: 'flex', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => openEditDialog(apiKey)}
                          sx={{ flex: 1, minWidth: 80, borderRadius: 2 }}
                        >
                          {t('settings.apiKeys.buttons.edit')}
                        </Button>
                        {apiKey.service === 'mistral' && (
                          <Button
                            size="small"
                            color="primary"
                            startIcon={<SparkleIcon />}
                            onClick={() => openTestDialog(apiKey)}
                            sx={{ flex: 1, minWidth: 80, borderRadius: 2 }}
                          >
                            {t('settings.apiKeys.buttons.test')}
                          </Button>
                        )}
                        <Button
                          size="small"
                          color={apiKey.is_active ? 'warning' : 'success'}
                          onClick={() => handleToggleStatus(apiKey)}
                          sx={{ flex: 1, minWidth: 80, borderRadius: 2 }}
                        >
                          {apiKey.is_active ? t('settings.apiKeys.buttons.deactivate') : t('settings.apiKeys.buttons.activate')}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => openDeleteDialog(apiKey)}
                          sx={{ flex: 1, minWidth: 80, borderRadius: 2 }}
                        >
                          {t('settings.apiKeys.buttons.delete')}
                        </Button>
                      </Box>
                    </CardActions>
                  </Card>
                </motion.div>
              ))}
            </Box>
          )}
        </AnimatePresence>

        {/* Add API Key Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <KeyIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('settings.apiKeys.add.title')}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('settings.apiKeys.add.service')}</InputLabel>
                <Select
                  value={formData.service}
                  onChange={(e) => setFormData(prev => ({ ...prev, service: e.target.value as any }))}
                  label={t('settings.apiKeys.add.service')}
                >
                  {serviceOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getServiceIcon(option.value)}
                        <Box>
                          <Typography variant="body1">{option.label}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {option.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={t('settings.apiKeys.add.apiKey')}
                value={formData.api_key}
                onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                type={showApiKey ? 'text' : 'password'}
                placeholder={t('settings.apiKeys.add.placeholder')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowApiKey(!showApiKey)}
                        edge="end"
                      >
                        {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {isCompanyDashboard && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t('settings.apiKeys.add.note.company')}
                </Alert>
              )}
              
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>{t('settings.apiKeys.add.noteTitle') || 'Note:'}</strong> {t('settings.apiKeys.add.note')}
                </Typography>
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={() => setAddDialogOpen(false)}
              disabled={formLoading}
              sx={{ borderRadius: 2 }}
            >
              {t('settings.apiKeys.common.cancel')}
            </Button>
            <Button
              onClick={handleAddApiKey}
              variant="contained"
              disabled={formLoading}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                },
                borderRadius: 2,
                px: 3,
              }}
            >
              {formLoading ? t('settings.apiKeys.add.adding') : t('settings.apiKeys.add.submit')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit API Key Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('settings.apiKeys.edit.title')}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              <TextField
                fullWidth
                label={t('settings.apiKeys.edit.service')}
                value={serviceOptions.find(s => s.value === formData.service)?.label || formData.service}
                disabled
                sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
              />

              <TextField
                fullWidth
                label="API Key"
                value={formData.api_key}
                onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                type={showApiKey ? 'text' : 'password'}
                placeholder="Enter your API key"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowApiKey(!showApiKey)}
                        edge="end"
                      >
                        {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={() => setEditDialogOpen(false)}
              disabled={formLoading}
              sx={{ borderRadius: 2 }}
            >
              {t('settings.apiKeys.common.cancel')}
            </Button>
            <Button
              onClick={handleEditApiKey}
              variant="contained"
              disabled={formLoading}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                },
                borderRadius: 2,
                px: 3,
              }}
            >
              {formLoading ? t('settings.apiKeys.edit.updating') : t('settings.apiKeys.edit.submit')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteIcon color="error" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('settings.apiKeys.delete.title')}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              {t('settings.apiKeys.delete.confirm')}
            </Typography>
            {selectedApiKey && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>{t('settings.apiKeys.delete.service')}</strong> {serviceOptions.find(s => s.value === selectedApiKey.service)?.label}<br />
                  <strong>{t('settings.apiKeys.delete.type')}</strong> {selectedApiKey.company_id ? t('settings.apiKeys.delete.type.company') : t('settings.apiKeys.delete.type.personal')}
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={formLoading}
              sx={{ borderRadius: 2 }}
            >
              {t('settings.apiKeys.common.cancel')}
            </Button>
            <Button
              onClick={handleDeleteApiKey}
              variant="contained"
              color="error"
              disabled={formLoading}
              sx={{ borderRadius: 2, px: 3 }}
            >
              {formLoading ? t('settings.apiKeys.delete.deleting') : t('settings.apiKeys.delete.submit')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* API Test Popup */}
        <ApiTestPopup
          open={testDialogOpen}
          onClose={() => setTestDialogOpen(false)}
          apiKey={testApiKey}
          service={testService}
        />
      </motion.div>
    </Box>
  );
};

export default ApiKeysTab;
